<script setup lang="ts">
import { computed, onMounted } from 'vue';
import { storeToRefs } from 'pinia';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useQuasar } from 'quasar';
import ReorderableTable from '@/components/ReorderableTable.vue';
import InfoBox from '@/components/base/InfoBox.vue';

import type { MailingCampaignDto } from '@/services/types/api.types';
import { useCommunityStore, useMailingCampaignStore, useMailingListStore } from '@/stores';
import CreateMailingCampaignDialog from '@/components/dialogs/CreateMailingCampaignDialog.vue';
import { getLanguageEnabled, getTranslation } from '@/composables/data-display-utils';
import { confirmDelete } from '@/helpers/dialogs.helper';
const router = useRouter();
const { t } = useI18n();
const $q = useQuasar();

const mailingCampaignStore = useMailingCampaignStore();
mailingCampaignStore.fetchMailingCampaigns();
const communityStore = useCommunityStore();
const { community } = storeToRefs(communityStore);
const { isLoading, mailingCampaigns } = storeToRefs(mailingCampaignStore);
const mailingListStore = useMailingListStore();

const languageEnabled = computed(() => getLanguageEnabled(community.value));

const columns = computed(() => [
  {
    name: 'title',
    label: t('global.label.title'),
    field: (row: MailingCampaignDto) => getTranslation(row, 'title', languageEnabled.value),
    align: 'left',
  },
  {
    name: 'status',
    label: t('global.label.status'),
    field: (row: MailingCampaignDto) => getStatus(row),
    align: 'left',
  },
]);

function getStatus(mailingCampaign: MailingCampaignDto) {
  const status = mailingCampaignStore.getStatuses();
  return status.find((s) => s.value === mailingCampaign.status).label;
}

const handleRowClick = (row: MailingCampaignDto) => {
  router.push({ name: 'mailing_campaign_detail', params: { id: row.id } });
};

const handleAddRow = () => {
  const campaign: MailingCampaignDto = mailingCampaignStore.createMailingCampaign();

  $q.dialog({
    component: CreateMailingCampaignDialog,
    componentProps: {
      campaign,
      languageEnabled: languageEnabled.value,
    },
  });
};

const handleDeleteRow = (row: MailingCampaignDto) => {
  if (!row.id) {
    console.warn('Row does not have an id');
    return;
  }

  confirmDelete(t('campaign.name')).onOk(async () => {
    await mailingCampaignStore.deleteMailingCampaign(row.id!);
  });
};
onMounted(async () => {
  await mailingListStore.fetchMailingLists();
});
</script>

<template>
  <q-page class="campaigns-list">
    <h1 class="title">{{ t('campaign.campaigns') }}</h1>
    <info-box type="info" :title="$t('campaign.infoBox.title')" :description="$t('campaign.infoBox.content')" />
    <reorderable-table
      :rows="mailingCampaigns"
      :columns="columns"
      :loading="isLoading"
      itemKey="id"
      hideBottom
      :disableOrderable="true"
      @row-click="handleRowClick"
      @add-row="handleAddRow"
      @delete-row="handleDeleteRow"
    />
  </q-page>
</template>
<style lang="scss">
.campaigns-list {
  padding: 16px;

  .title {
    margin: 0 10px !important;
  }
}
</style>
